import {
	type ClassValue,
	clsx,
} from 'clsx';
import {
	extendTailwindMerge,
	validators,
} from 'tailwind-merge';
import {
	BORDER_COLOR_ALIASES,
	CONTROL_GAP_NAMES,
	CONTROL_HEIGHT_NAMES,
	CONTROL_MIN_HEIGHT_NAMES,
	CONTROL_PADDING_NAMES,
	CONTROL_PADDING_X_NAMES,
	CONTROL_PADDING_Y_NAMES,
	DURATION_NAMES,
	EASE_NAMES,
	FONT_FAMILY_NAMES,
	FONT_SIZE_NAMES,
	FONT_WEIGHT_NAMES,
	ICON_HIT_AREA_NAMES,
	ICON_SIZE_NAMES,
	ICON_TEXT_GAP_NAMES,
	RADIUS_NAMES,
	ROUNDED_B_NAMES,
	ROUNDED_E_NAMES,
	ROUNDED_S_NAMES,
	ROUNDED_T_NAMES,
	SEMANTIC_COLORS,
	SHADOW_NAMES,
	TEXT_COLOR_ALIASES,
	Z_INDEX_NAMES,
} from './generated/tailwind-semantic-tokens';

const RADIUS_VALUE_PARTS = [
	validators.isInteger,
	validators.isFraction,
	validators.isArbitraryValue,
	validators.isArbitraryVariable,
] as const;

const twMerge = extendTailwindMerge<
	| 'control-height'
	| 'control-min-height'
	| 'control-padding'
	| 'control-padding-x'
	| 'control-padding-y'
	| 'control-gap'
	| 'icon-size'
	| 'icon-hit-area'
	| 'icon-text-gap'
>({
	extend: {
		classGroups: {
			/*
			 * Typography recipes (font-size + line-height + font-weight) are
			 * listed alongside raw text-* sizes so they dedupe against each
			 * other. Mixing type-* with font-bold or leading-* is supported
			 * but does not dedupe — the last utility wins via CSS order.
			 */
			'font-size': [...FONT_SIZE_NAMES],
			'font-weight': [...FONT_WEIGHT_NAMES],
			'font-family': [...FONT_FAMILY_NAMES],
			'text-color': [
				...TEXT_COLOR_ALIASES.map(c => `text-${c}`),
				...SEMANTIC_COLORS.map(c => `text-${c}`),
			],
			'bg-color': SEMANTIC_COLORS.map(c => `bg-${c}`),
			'border-color': [
				...BORDER_COLOR_ALIASES.map(c => `border-${c}`),
				...SEMANTIC_COLORS.map(c => `border-${c}`),
			],
			'ring-color': SEMANTIC_COLORS.map(c => `ring-${c}`),
			'outline-color': SEMANTIC_COLORS.map(c => `outline-${c}`),
			'divide-color': SEMANTIC_COLORS.map(c => `divide-${c}`),
			'fill': SEMANTIC_COLORS.map(c => `fill-${c}`),
			'stroke': SEMANTIC_COLORS.map(c => `stroke-${c}`),

			/*
			 * Semantic radius — dedupes against each other and the
			 * functional `rounded-N` integer utilities. `rounded-full`
			 * is part of this group (semantic bucket: pill / avatar) and
			 * dedupes against the others.
			 */
			'rounded': [
				{
					rounded: [
						...RADIUS_NAMES,
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-s': [
				{
					'rounded-s': [
						...ROUNDED_S_NAMES,
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-e': [
				{
					'rounded-e': [
						...ROUNDED_E_NAMES,
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-t': [
				{
					'rounded-t': [
						...ROUNDED_T_NAMES,
						...RADIUS_VALUE_PARTS,
					],
				},
			],
			'rounded-b': [
				{
					'rounded-b': [
						...ROUNDED_B_NAMES,
						...RADIUS_VALUE_PARTS,
					],
				},
			],

			/* Semantic elevation. */
			'shadow': [...SHADOW_NAMES],

			/* Semantic z-index layers. */
			'z': [...Z_INDEX_NAMES],

			/* Semantic transition-duration. */
			'duration': [...DURATION_NAMES],

			/* Semantic transition-timing-function. */
			'ease': [...EASE_NAMES],

			/* Control sizing and spacing. */
			'control-height': [...CONTROL_HEIGHT_NAMES],
			'control-min-height': [...CONTROL_MIN_HEIGHT_NAMES],
			'control-padding': [...CONTROL_PADDING_NAMES],
			'control-padding-x': [...CONTROL_PADDING_X_NAMES],
			'control-padding-y': [...CONTROL_PADDING_Y_NAMES],
			'control-gap': [...CONTROL_GAP_NAMES],

			/*
			 * Icon size — bundled width + height + min-* utilities. Dedupes
			 * against itself; not part of width/height groups so adjacent
			 * `w-*` / `h-*` utilities still apply if a component needs to
			 * override one axis.
			 */
			'icon-size': [...ICON_SIZE_NAMES],
			'icon-hit-area': [...ICON_HIT_AREA_NAMES],
			'icon-text-gap': [...ICON_TEXT_GAP_NAMES],
		},
	},
});

/**
 * Join conditional class values and dedupe conflicting Tailwind utilities,
 * including the semantic token utility groups defined for this design system.
 */
export default function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs));
}
